import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMapboxUsage, getEvents } from '../services/analytics';
import MapboxUsageGauge from './components/MapboxUsageGauge';
import AlertBanner from './components/AlertBanner';

function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout, isLoading } = useAuth();
  const [mapboxUsage, setMapboxUsage] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const loadData = () => {
      const mapboxData = getMapboxUsage();
      setMapboxUsage(mapboxData);
    };
    loadData();
  }, [refreshKey]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login', { replace: true });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const mapboxPercentage = mapboxUsage ? (mapboxUsage.totalLoads / 50000) * 100 : 0;

  // Calculate daily average from last 7 days
  const history = [];
  for (let i = 6; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);
    const dayEvents = getEvents({ type: 'mapbox_load', startDate: dayStart, endDate: dayEnd });
    history.push(dayEvents.length);
  }
  const daysWithData = history.filter(d => d > 0).length;
  const dailyAverage = daysWithData > 0 ? history.reduce((a, b) => a + b, 0) / daysWithData : 0;

  return (
    <div className="min-h-screen bg-surface">
      {/* Header */}
      <header className="bg-surface-container/80 backdrop-blur-xl shadow-[0px_12px_32px_rgba(0,0,0,0.4)] sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-primary text-2xl">monitoring</span>
              <div>
                <h1 className="text-lg md:text-xl font-headline text-on-surface">Admin</h1>
                <p className="text-slate-500 text-xs font-body">Migration Journal</p>
              </div>
            </div>
            <div className="flex items-center gap-2 md:gap-3">
              <span className="text-on-surface-variant text-xs hidden sm:inline font-label">{user}</span>
              <button onClick={() => setRefreshKey(k => k + 1)} className="p-2 text-on-surface-variant hover:text-on-surface hover:bg-white/10 rounded-lg transition-colors" title="Refresh">
                <span className="material-symbols-outlined text-lg">refresh</span>
              </button>
              <button onClick={handleLogout} className="px-3 py-1.5 bg-error/10 hover:bg-error/20 text-error text-xs font-medium rounded-lg transition-colors font-label">
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Alert Banner */}
        {mapboxPercentage >= 80 && (
          <AlertBanner
            type={mapboxPercentage >= 95 ? 'critical' : 'warning'}
            message={
              mapboxPercentage >= 95
                ? `Critical: Mapbox usage at ${mapboxPercentage.toFixed(1)}%! You may incur charges soon.`
                : `Warning: Mapbox usage at ${mapboxPercentage.toFixed(1)}%. Approaching free tier limit.`
            }
          />
        )}

        {/* Mapbox Usage — the main reason this dashboard exists */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <MapboxUsageGauge
            current={mapboxUsage?.totalLoads || 0}
            limit={50000}
            dailyAverage={dailyAverage}
            daysTracked={daysWithData}
          />

          <div className="bg-surface-container-highest/60 backdrop-blur-sm rounded-2xl p-6 shadow-[0px_12px_32px_rgba(0,0,0,0.2)]">
            <h3 className="text-on-surface font-headline text-base mb-6">Quick Links</h3>
            <div className="space-y-3">
              <a
                href="https://dash.cloudflare.com/?to=/:account/web-analytics"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors group"
              >
                <span className="material-symbols-outlined text-tertiary">analytics</span>
                <div>
                  <p className="text-on-surface text-sm font-medium group-hover:text-primary transition-colors">Cloudflare Web Analytics</p>
                  <p className="text-slate-500 text-xs font-body">Page views, visitors, referrers, countries</p>
                </div>
                <span className="material-symbols-outlined text-slate-600 text-sm ml-auto">open_in_new</span>
              </a>

              <a
                href="https://account.mapbox.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors group"
              >
                <span className="material-symbols-outlined text-secondary">map</span>
                <div>
                  <p className="text-on-surface text-sm font-medium group-hover:text-primary transition-colors">Mapbox Dashboard</p>
                  <p className="text-slate-500 text-xs font-body">API tokens, usage details, billing</p>
                </div>
                <span className="material-symbols-outlined text-slate-600 text-sm ml-auto">open_in_new</span>
              </a>

              <a
                href="https://github.com/stewartburton/wildlife-migration-tracker"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors group"
              >
                <span className="material-symbols-outlined text-primary">code</span>
                <div>
                  <p className="text-on-surface text-sm font-medium group-hover:text-primary transition-colors">GitHub Repository</p>
                  <p className="text-slate-500 text-xs font-body">Source code, issues, deployments</p>
                </div>
                <span className="material-symbols-outlined text-slate-600 text-sm ml-auto">open_in_new</span>
              </a>

              <a
                href="https://dash.cloudflare.com/?to=/:account/pages/view/wildlife-migration-tracker"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors group"
              >
                <span className="material-symbols-outlined text-primary-fixed-dim">cloud</span>
                <div>
                  <p className="text-on-surface text-sm font-medium group-hover:text-primary transition-colors">Cloudflare Pages</p>
                  <p className="text-slate-500 text-xs font-body">Deployments, domains, settings</p>
                </div>
                <span className="material-symbols-outlined text-slate-600 text-sm ml-auto">open_in_new</span>
              </a>
            </div>
          </div>
        </div>

        {/* Back to Site */}
        <div className="text-center pt-4">
          <a href="/" className="text-slate-400 hover:text-primary text-sm transition-colors inline-flex items-center gap-2 font-body">
            <span className="material-symbols-outlined text-sm">arrow_back</span>
            Back to Migration Journal
          </a>
        </div>
      </main>
    </div>
  );
}

export default DashboardPage;
